/**
 * useSchedulingKpi - Hook para KPIs de agendamento
 * Retorna totais, atrasados, agendados, concluídos e breakdown por tipo
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-14 v1.0.0 - Implementação inicial
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';
import { createLogger } from '@/lib/logger';

// Logger dedicado para este hook
const logger = createLogger('SchedulingKpi');

/** Dados retornados pelo hook */
export interface SchedulingKpiData {
  /** Total de agendamentos (atividades + bookings online + standalone) */
  totalScheduled: number;
  /** Atrasados: scheduled_at < NOW() e não concluídos */
  overdueCount: number;
  /** Agendados: scheduled_at >= NOW() e não concluídos */
  upcomingCount: number;
  /** Concluídos no período */
  completedCount: number;
  /** Porcentagem de conclusão */
  completionPercentage: number;
  /** Bookings online confirmados (inclui standalone) */
  onlineCount: number;
  /** Atividades tipo meeting */
  meetingCount: number;
  /** Atividades tipo task */
  taskCount: number;
  /** Bookings standalone (sem vínculo com deal) - v1.3.0 */
  standaloneCount: number;
}

/** Dados padrão para estado inicial */
const DEFAULT_DATA: SchedulingKpiData = {
  totalScheduled: 0,
  overdueCount: 0,
  upcomingCount: 0,
  completedCount: 0,
  completionPercentage: 0,
  onlineCount: 0,
  meetingCount: 0,
  taskCount: 0,
  standaloneCount: 0,
};

/** Opções do hook */
export interface SchedulingKpiOptions {
  startDate?: Date;
  endDate?: Date;
  workspaceId?: string | null;
  sellerId?: string | null;
  performedByUserId?: string | null;
}

/**
 * Hook para buscar KPIs de agendamento via RPC get_scheduling_kpi
 */
export function useSchedulingKpi(options?: SchedulingKpiOptions) {
  const { currentTenant } = useAuth();

  // Formata datas para enviar à RPC
  const formattedStart = options?.startDate ? format(options.startDate, 'yyyy-MM-dd') : null;
  const formattedEnd = options?.endDate ? format(options.endDate, 'yyyy-MM-dd') : null;

  const queryKey = [
    'scheduling-kpi',
    currentTenant?.id,
    formattedStart,
    formattedEnd,
    options?.workspaceId ?? null,
    options?.sellerId ?? null,
    options?.performedByUserId ?? null,
  ];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<SchedulingKpiData> => {
      if (!currentTenant?.id) return DEFAULT_DATA;

      logger.debug('Buscando KPIs de agendamento', {
        startDate: formattedStart,
        endDate: formattedEnd,
      });

      const { data, error } = await supabase.rpc('get_scheduling_kpi', {
        p_start_date: formattedStart,
        p_end_date: formattedEnd,
        p_workspace_id: options?.workspaceId ?? null,
        p_seller_id: options?.sellerId ?? null,
        p_user_id: options?.performedByUserId ?? null,
      } as Record<string, unknown>);

      if (error) {
        logger.error('Erro na RPC get_scheduling_kpi', { error: error.message });
        throw error;
      }

      // RPC retorna array, pega primeiro registro
      const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
      if (!row) return DEFAULT_DATA;

      return {
        totalScheduled: Number(row.total_scheduled) || 0,
        overdueCount: Number(row.overdue_count) || 0,
        upcomingCount: Number(row.upcoming_count) || 0,
        completedCount: Number(row.completed_count) || 0,
        completionPercentage: Number(row.completion_percentage) || 0,
        onlineCount: Number(row.online_count) || 0,
        meetingCount: Number(row.meeting_count) || 0,
        taskCount: Number(row.task_count) || 0,
        standaloneCount: Number(row.standalone_count) || 0,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    kpiData: data ?? DEFAULT_DATA,
    isLoading,
    error,
    /** Refetch manual dos KPIs */
    refetch,
  };
}
